import React, { useRef, useState } from "react";
import {
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import Modal from "../../../UI/Modal";
import { downloadRdvBulkTemplate } from "../../../lib/rdvBulkInput/exportTemplate";
import { RANDOM_ROW_DEFAULT_COUNT } from "../../../lib/rdvBulkInput/constants";
import { sendSupportAppSmsLinks } from "../../../lib/rdvBulkInput/sendSupportAppSmsLinks";
import useRdvBulkInputState from "./useRdvBulkInputState";
import cls from "./RdvBulkInput.module.css";

function rowResultChip(row) {
  if (row.saveStatus === "saved")
    return <Chip size="small" label="등록완료" color="success" />;
  if (row.saveStatus === "failed")
    return <Chip size="small" label="오류" color="error" variant="outlined" />;
  if (row.rowStatus === "error")
    return <Chip size="small" label="오류" color="error" variant="outlined" />;
  if (row.rowStatus === "normal")
    return <Chip size="small" label="정상" color="success" variant="outlined" />;
  return <Chip size="small" label="입력전" variant="outlined" />;
}

export default function RdvBulkInputModal({ apiAddr, open, onClose, onSaved }) {
  const state = useRdvBulkInputState(apiAddr);
  const {
    rows,
    uploadedFileName,
    validatedAt,
    isValidating,
    isSaving,
    saveProgress,
    validationCounts,
    defaultWorkplace,
    setDefaultWorkplace,
    workplaceOptions,
    loadExcelFile,
    runValidation,
    updateField,
    deleteRow,
    clearRows,
    generateRandomRows,
    saveRows,
  } = state;

  const fileRef = useRef(null);
  const [replyNumber, setReplyNumber] = useState(() =>
    typeof localStorage !== "undefined" && localStorage.rdvSmsReplyNumber
      ? localStorage.rdvSmsReplyNumber
      : "033-249-5664",
  );
  const [smsSending, setSmsSending] = useState(false);

  if (!open) return null;

  const dsid =
    typeof sessionStorage !== "undefined"
      ? (sessionStorage.ondsid || "").trim()
      : "";
  const dsnm =
    typeof sessionStorage !== "undefined"
      ? (sessionStorage.onname || "").trim()
      : "";

  const canSave =
    rows.length > 0 &&
    validationCounts.errors === 0 &&
    validatedAt &&
    !isSaving;

  const handleSave = async () => {
    if (
      !window.confirm(
        "검증된 지원인력을 출근 등록하고 지원앱 링크 문자를 함께 발송하시겠습니까?",
      )
    ) {
      return;
    }
    try {
      const results = await saveRows();
      const savedRows = results.filter((r) => r.saveStatus === "saved");
      const smsTargets = savedRows.filter((r) => String(r.pphone || "").trim());

      let smsSummary = "";
      const missingAttSnum = savedRows.filter(
        (r) => String(r.pphone || "").trim() && !r.attSnum,
      ).length;
      if (smsTargets.length > 0) {
        setSmsSending(true);
        const { ok, fail, missingAttLink, replyNum } = await sendSupportAppSmsLinks({
          apiAddr,
          dsid,
          dsnm,
          rows: smsTargets,
          replyNumber,
        });
        try {
          localStorage.rdvSmsReplyNumber = replyNum;
        } catch {
          /* ignore */
        }
        setSmsSending(false);
        smsSummary = `\n문자 발송: 성공 ${ok}건, 실패 ${fail}건`;
        if (missingAttLink > 0) {
          smsSummary += `\n⚠ 연동키(a) 미포함 ${missingAttLink}건 — 앱에서 빈 폼으로 열릴 수 있습니다.`;
        }
      }
      if (missingAttSnum > 0) {
        smsSummary += `\n⚠ 출퇴근 번호 미수신 ${missingAttSnum}건 — API 서버 배포 후 다시 저장·발송하세요.`;
      }

      onSaved?.();
      alert(`저장이 완료되었습니다. (출근 ${savedRows.length}건)${smsSummary}`);
    } catch (e) {
      setSmsSending(false);
      alert(e.message || "저장 중 오류가 발생했습니다.");
    }
  };

  const handleBulkSmsLink = async () => {
    const targets = rows.filter(
      (r) =>
        String(r.pphone || "").trim() &&
        r.rowStatus !== "error" &&
        (r.rdvId || r.siteId),
    );
    if (targets.length === 0) {
      alert("전화번호가 있고 검증 통과한 행이 없습니다.");
      return;
    }
    if (
      !window.confirm(
        `${targets.length}명에게 지원앱 링크 문자를 발송할까요?`,
      )
    ) {
      return;
    }

    setSmsSending(true);
    const { ok, fail, replyNum } = await sendSupportAppSmsLinks({
      apiAddr,
      dsid,
      dsnm,
      rows: targets,
      replyNumber,
    });

    try {
      localStorage.rdvSmsReplyNumber = replyNum;
    } catch {
      /* ignore */
    }

    setSmsSending(false);
    alert(`문자 발송 완료: 성공 ${ok}건, 실패 ${fail}건`);
  };

  return (
    <Modal onClose={onClose} modalSort="rdvinput">
      <div className={cls.root}>
        <header className={cls.header}>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              fontSize: "1.1rem",
              cursor: "pointer",
              fontWeight: 800,
            }}
          >
            ✕
          </button>
          <span className={cls.headerTitle}>지원인력 일괄입력</span>
          <span style={{ width: 24 }} />
        </header>

        <div className={cls.toolbar}>
          <button
            type="button"
            className={cls.btnAction}
            style={{ background: "#42a5f5", color: "#fff", border: "none" }}
            onClick={() => fileRef.current?.click()}
          >
            엑셀 파일 선택
          </button>
          <input
            ref={fileRef}
            type="file"
            accept=".xlsx"
            hidden
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) loadExcelFile(f);
              e.target.value = "";
            }}
          />
          {uploadedFileName && (
            <span style={{ fontSize: "0.78rem", fontWeight: 700, color: "#f57f17" }}>
              ✓ {uploadedFileName}
            </span>
          )}
          <button
            type="button"
            className={cls.btnAction}
            onClick={() => downloadRdvBulkTemplate(defaultWorkplace)}
          >
            양식 다운로드
          </button>
          <button
            type="button"
            className={`${cls.btnAction} ${cls.btnValidate}`}
            onClick={runValidation}
            disabled={isValidating || rows.length === 0}
          >
            {isValidating ? "검증 중…" : "검증 실행"}
          </button>
          <button
            type="button"
            className={cls.btnAction}
            onClick={() => {
              if (window.confirm("모든 행을 삭제할까요?")) clearRows();
            }}
          >
            전체 지우기
          </button>
          <label style={{ fontSize: "0.78rem", fontWeight: 700 }}>
            기본 근무지
            <select
              value={defaultWorkplace}
              onChange={(e) => setDefaultWorkplace(e.target.value)}
              style={{ marginLeft: 6, fontSize: "0.78rem", maxWidth: 180 }}
            >
              <option value="">— 선택 —</option>
              {workplaceOptions.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
          </label>
          <div className={cls.toolbarSpacer} />
          <button
            type="button"
            className={`${cls.btnAction} ${cls.btnRandom}`}
            onClick={() => {
              if (
                rows.length > 0 &&
                !window.confirm(
                  `기존 행을 지우고 랜덤 데이터 ${RANDOM_ROW_DEFAULT_COUNT}건을 생성할까요?`,
                )
              ) {
                return;
              }
              generateRandomRows();
            }}
          >
            랜덤생성
          </button>
        </div>

        <div className={cls.smsPanel}>
          <span style={{ fontWeight: 700 }}>지원앱 링크 문자</span>
          <span style={{ fontSize: "0.72rem", color: "#666" }}>
            검증된 행의 전화번호로 출근등록 URL 발송
          </span>
          <input
            value={replyNumber}
            onChange={(e) => setReplyNumber(e.target.value)}
            placeholder="회신번호"
            style={{
              width: "9em",
              padding: "3px 6px",
              borderRadius: 4,
              border: "1px solid #ccc",
            }}
          />
          <button
            type="button"
            className={cls.btnAction}
            style={{ background: "#66bb6a", color: "#fff", border: "none" }}
            onClick={handleBulkSmsLink}
            disabled={smsSending || rows.length === 0}
          >
            {smsSending ? "발송 중…" : "링크 문자 일괄 발송"}
          </button>
        </div>

        <TableContainer className={cls.gridWrap}>
          <Table size="small" stickyHeader sx={{ borderCollapse: "collapse" }}>
            <TableHead>
              <TableRow>
                <TableCell className={cls.colHeader}>연번</TableCell>
                <TableCell className={cls.colHeader}>근무지</TableCell>
                <TableCell className={cls.colHeader}>구분</TableCell>
                <TableCell className={cls.colHeader}>소속</TableCell>
                <TableCell className={cls.colHeader}>부서</TableCell>
                <TableCell className={cls.colHeader}>계급</TableCell>
                <TableCell className={cls.colHeader}>이름*</TableCell>
                <TableCell className={cls.colHeader}>전화번호</TableCell>
                <TableCell className={cls.colHeader}>임무</TableCell>
                <TableCell className={cls.colHeader}>결과</TableCell>
                <TableCell className={cls.colHeader}>삭제</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={11} align="center" sx={{ py: 4, color: "#888" }}>
                    엑셀을 불러오거나 랜덤생성·양식을 참고해 데이터를 준비하세요.
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell align="center" sx={{ fontSize: "0.75rem" }}>
                      {row.seq}
                    </TableCell>
                    {[
                      ["workplace", "text"],
                      ["personTypeLabel", "select"],
                      ["porg", "text"],
                      ["pdept", "text"],
                      ["prank", "text"],
                      ["pname", "text"],
                      ["pphone", "text"],
                      ["dutyDesc", "text"],
                    ].map(([key, kind]) => {
                      const status = row.fieldStatus?.[key];
                      const cellClass = [
                        cls.cellWrap,
                        status === "error" ? cls.cellError : "",
                        status === "ok" ? cls.cellOk : "",
                      ]
                        .filter(Boolean)
                        .join(" ");
                      return (
                        <TableCell
                          key={key}
                          className={cellClass}
                          title={row.fieldMessages?.[key] || ""}
                        >
                          {kind === "select" ? (
                            <select
                              className={cls.cellSelect}
                              value={row.personTypeLabel}
                              onChange={(e) =>
                                updateField(row.id, key, e.target.value)
                              }
                            >
                              <option value="소방">소방</option>
                              <option value="비소방">비소방</option>
                            </select>
                          ) : (
                            <input
                              className={cls.cellInput}
                              value={row[key] ?? ""}
                              onChange={(e) =>
                                updateField(row.id, key, e.target.value)
                              }
                              list={
                                key === "workplace"
                                  ? "rdv-workplace-list"
                                  : undefined
                              }
                            />
                          )}
                        </TableCell>
                      );
                    })}
                    <TableCell align="center">{rowResultChip(row)}</TableCell>
                    <TableCell align="center">
                      <button
                        type="button"
                        onClick={() => deleteRow(row.id)}
                        style={{
                          padding: "1px 6px",
                          fontSize: "0.72rem",
                          background: "#e53935",
                          color: "#fff",
                          border: "none",
                          borderRadius: 3,
                          cursor: "pointer",
                        }}
                      >
                        ✕
                      </button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          <datalist id="rdv-workplace-list">
            {workplaceOptions.map((name) => (
              <option key={name} value={name} />
            ))}
          </datalist>
        </TableContainer>

        <div className={cls.footer}>
          <span className={cls.hint}>
            {validationCounts.errors > 0
              ? `오류 ${validationCounts.errors}건 — 수정 후 재검증`
              : isSaving
                ? `저장 중… ${saveProgress.done}/${saveProgress.total}`
                : "이름·근무지 필수 · 저장 시 전화번호로 링크 문자 자동 발송"}
          </span>
          <button
            type="button"
            className={cls.btnPrimary}
            disabled={!canSave || smsSending}
            onClick={handleSave}
          >
            {isSaving || smsSending ? "처리 중…" : "출근 등록·문자 발송"}
          </button>
        </div>
      </div>
    </Modal>
  );
}
